"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LightshowV2Converter = exports.LightshowV2Extractor = exports.LightshowV2Canvasser = exports.LightshowV2Layout = exports.LightshowV2 = void 0;
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const logger_1 = require("../util/logger");
const lightshow_utils_1 = require("./lightshow_utils");
var FrameSide = streem_sdk_protobuf_1.streem.room.CameraFrame.FrameSide;
const operators_1 = require("rxjs/operators");
// For each stripe in Lightshow V2, embedded on the short edge, we expect 8px at 1280px resolution.
const V2_STRIPE_HEIGHT_RATIO = 8.0 / 1280.0;
const BITS_PER_STRIPE = 90.0;
// Debug flags
const KEEP_LIGHTSHOW_VISIBLE = false;
const LIGHTSHOW_TIMING_OUTPUT = false;
const RENDER_LIGHTSHOW_STRIPES_SEPARATELY_FOR_DEBUG = false;
/**
 * In LightshowV2, there are 8 total numbers embedded in the lightshow bits.
 * The 8 x 32 data bits are encoded in the first 64 sections of the 4 stripes;
 * the remaining sections are filled with zeros.
 *
 * I.e. (landscape):
 *          0   64                      128   192
 *          1   65                      129   193
 *          2   66                      130   194
 *          ...
 *         63  127                      191   255
 *        000  000                      000   000
 *          ...
 *        000  000                      000   000
 *
 * The encoding always happens with four stripes, two on each end of the short edges.
 * So in Portrait, the stripes are horizontal on the top and bottom edges, and in
 * Landscape, the stripes are vertical on the left and right edges.
 *
 * In Portrait, the bits are encoded with the lowest-order on the left (opposite what you'd
 * expect if you looked at the bits visually).  In Landscape, they are encoded with the
 * lowest-order on the top.
 *
 * The eight 32-bit values represent the frame number (at index 0, as a uint32), and the rotation
 * and pose in the next seven values, interpreted as float32s, with rotation (x, y, z, w) at
 * indexes (2, 3, 4, 1), and pose (x, y, z) values at indexes (5, 6, 7)
 */
class LightshowV2 {
    constructor() {
        this.count = 0;
        this.statsInterval = 60;
        this.lightshowTotalTime = 0;
        this.canvasTotalTime = 0;
        this.unsubscribe = new rxjs_1.Subject();
        this.canvas = document.createElement('canvas');
        this.layout = new LightshowV2Layout();
        this.canvasser = new LightshowV2Canvasser(this.layout, this.canvas);
        this.extractor = new LightshowV2Extractor(this.layout, this.canvasser);
        this.converter = new LightshowV2Converter(this.layout);
        logger_1.logger.info('[LightshowV2] Using LightshowV2');
    }
    connect(dataTrack) {
        dataTrack
            .pipe((0, operators_1.filter)(msg => msg.type === 'cameraFrame' && 'cameraFrame' in msg), (0, operators_1.map)(msg => msg.cameraFrame), (0, operators_1.takeUntil)(this.unsubscribe))
            .subscribe(frame => {
            this.latestDataTrackFrame = frame;
        });
        if (RENDER_LIGHTSHOW_STRIPES_SEPARATELY_FOR_DEBUG) {
            const d = document.createElement('div');
            d.style.position = 'absolute';
            d.style.top = '0';
            d.style.left = '0';
            d.style.width = '360px';
            d.style.height = '360px';
            d.style.zIndex = '1000000';
            d.style.backgroundColor = '#ddd';
            document.body.appendChild(d);
            d.appendChild(this.canvas);
            this.debugElement = d;
        }
    }
    disconnect() {
        var _a;
        logger_1.logger.debug('[LightshowV2] Clearing subscriptions');
        this.unsubscribe.next(undefined);
        this.canvas.remove();
        (_a = this.debugElement) === null || _a === void 0 ? void 0 : _a.remove();
    }
    getLongEdgeOffset(longEdge) {
        if (KEEP_LIGHTSHOW_VISIBLE) {
            return 0;
        }
        else {
            const stripeHeight = Math.floor(longEdge * V2_STRIPE_HEIGHT_RATIO);
            return stripeHeight * 4;
        }
    }
    getLightshowAvoidingRect(videoElement) {
        if (KEEP_LIGHTSHOW_VISIBLE) {
            return {
                x: 0,
                y: 0,
                width: videoElement.videoWidth,
                height: videoElement.videoHeight,
            };
        }
        else {
            const dimensions = {
                width: videoElement.videoWidth,
                height: videoElement.videoHeight,
            };
            this.layout.updateLayout(dimensions);
            return this.layout.lightshowFreeRect();
        }
    }
    extractCameraFrame(videoElement) {
        // Reset stats at start of each interval
        if (LIGHTSHOW_TIMING_OUTPUT && this.count % this.statsInterval === 0) {
            this.canvasTotalTime = 0;
            this.lightshowTotalTime = 0;
        }
        const start = Date.now();
        const dimensions = {
            width: videoElement.videoWidth,
            height: videoElement.videoHeight,
        };
        // Update layout and transfer to canvas
        this.layout.updateLayout(dimensions);
        this.canvasser.transferLightshowToCanvas(videoElement, this.debugElement);
        this.canvasTotalTime += Date.now() - start;
        // Extract lightshow from canvas
        const integers = this.extractor.extractLightshowIntegers();
        const cameraFrame = this.converter.convertRecoveredIntegersToCamera(integers, this.latestDataTrackFrame);
        this.lightshowTotalTime += Date.now() - start;
        // Output stats occasionally
        if (LIGHTSHOW_TIMING_OUTPUT && ++this.count % Math.floor(this.statsInterval) === 0) {
            logger_1.logger.info(`[LightshowV2] Average processing time over last ${this.statsInterval} frames: ${(this.lightshowTotalTime / this.statsInterval).toFixed(2)}ms, canvas: ${(this.canvasTotalTime / this.statsInterval).toFixed(2)}ms`);
            logger_1.logger.info(`[LightshowV2] Last CameraFrame was number ${cameraFrame.frameNumber}, and had pose: ` +
                `${cameraFrame.cameraPose.position[0].toFixed(2)}, ` +
                `${cameraFrame.cameraPose.position[1].toFixed(2)}, ` +
                `${cameraFrame.cameraPose.position[2].toFixed(2)}`);
        }
        return cameraFrame;
    }
}
exports.LightshowV2 = LightshowV2;
/**
 * LightshowV2Layout understands where on the source canvas the lightshow can be found, and where
 * to render it on the destination canvas.
 */
class LightshowV2Layout {
    constructor() {
        this.sourceDimensions = { width: 0, height: 0 };
        // Whether the orientation is portrait (true), or landscape (false)
        this.isPortrait = true;
        // The number of pixels on the short edge and long edges
        this.shortEdge = 0;
        this.longEdge = 0;
        // The number of pixels height for each stripe.  In Portrait, this would be actual height, since the stripes are horizontal
        // on the short edge.  In Landscape, this height would represent a width value, since the stripes are vertical
        this.stripeHeight = 0;
        this.halfStripeHeight = 0;
        this.twoStripesHeight = 0;
        this.fourStripesHeight = 0;
        // The number of pixels wide for each bit.  In Portrait, this would be actual width, since the stripes are horizontal,
        // and the bits are side-by-side.  In Landscape, this value would represent a height value, since the stripes are vertical.
        this.bitWidth = 0;
        this.halfBitWidth = 0;
        // The expected canvas width and height for copying the lightshow pixels to.  In Portrait, the width would be full
        // short-edge width, with 4x stripe height in total height.  In Landscape, the width would be 4x stripe height in
        // total width, and full short-edge height.
        this.canvasWidth = 0;
        this.canvasHeight = 0;
    }
    updateLayout(dimensions) {
        if (dimensions.width === this.sourceDimensions.width &&
            dimensions.height === this.sourceDimensions.height) {
            return;
        }
        this.sourceDimensions = dimensions;
        this.isPortrait = dimensions.width < dimensions.height;
        this.shortEdge = this.isPortrait ? dimensions.width : dimensions.height;
        this.longEdge = this.isPortrait ? dimensions.height : dimensions.width;
        // V2 has four stripes of data, two on each side, the width of each being the long edge times the ratio.
        this.stripeHeight = Math.floor(this.longEdge * V2_STRIPE_HEIGHT_RATIO);
        this.halfStripeHeight = Math.floor(this.stripeHeight / 2.0);
        this.twoStripesHeight = this.stripeHeight * 2;
        this.fourStripesHeight = this.stripeHeight * 4;
        // Each stripe has 90 bits, split across the short-edge
        this.bitWidth = this.shortEdge / BITS_PER_STRIPE;
        this.halfBitWidth = Math.floor(this.bitWidth / 2.0);
        // The canvas will hold all four stripes of data
        this.canvasWidth = this.isPortrait ? this.shortEdge : this.fourStripesHeight;
        this.canvasHeight = this.isPortrait ? this.fourStripesHeight : this.shortEdge;
    }
    firstStripePairSourceRect() {
        return {
            x: 0,
            y: 0,
            width: this.isPortrait ? this.shortEdge : this.twoStripesHeight,
            height: this.isPortrait ? this.twoStripesHeight : this.shortEdge,
        };
    }
    firstStripePairDestinationRect() {
        return {
            x: 0,
            y: 0,
            width: this.isPortrait ? this.shortEdge : this.twoStripesHeight,
            height: this.isPortrait ? this.twoStripesHeight : this.shortEdge,
        };
    }
    secondStripePairSourceRect() {
        return {
            x: this.isPortrait ? 0 : this.longEdge - this.twoStripesHeight,
            y: this.isPortrait ? this.longEdge - this.twoStripesHeight : 0,
            width: this.isPortrait ? this.shortEdge : this.twoStripesHeight,
            height: this.isPortrait ? this.twoStripesHeight : this.shortEdge,
        };
    }
    secondStripePairDestinationRect() {
        return {
            x: this.isPortrait ? 0 : this.canvasWidth / 2,
            y: this.isPortrait ? this.canvasHeight / 2 : 0,
            width: this.isPortrait ? this.canvasWidth : this.canvasWidth / 2,
            height: this.isPortrait ? this.canvasHeight / 2 : this.canvasHeight,
        };
    }
    lightshowFreeRect() {
        return {
            x: this.isPortrait ? 0 : this.twoStripesHeight,
            y: this.isPortrait ? this.twoStripesHeight : 0,
            width: this.isPortrait ? this.shortEdge : this.longEdge - this.fourStripesHeight,
            height: this.isPortrait ? this.longEdge - this.fourStripesHeight : this.shortEdge,
        };
    }
    getBitX(stripe, bit) {
        if (this.isPortrait) {
            // In portrait, the bits are reversed from what you see visually on the screen (highest-order on right)
            return this.shortEdge - (bit * this.bitWidth + this.halfBitWidth);
        }
        else {
            return stripe * this.stripeHeight + this.halfStripeHeight;
        }
    }
    getBitY(stripe, bit) {
        if (this.isPortrait) {
            return stripe * this.stripeHeight + this.halfStripeHeight;
        }
        else {
            return bit * this.bitWidth + this.halfBitWidth;
        }
    }
}
exports.LightshowV2Layout = LightshowV2Layout;
/**
 * Class responsible for using the provided Layout and Canvas, and transferring the lightshow from the given
 * CanvasImageSource onto the canvas.  This class holds the reference to the CanvasRenderingContext2D for the canvas.
 */
class LightshowV2Canvasser {
    constructor(layout, canvas) {
        this.layout = layout;
        this.canvas = canvas;
    }
    transferLightshowToCanvas(source, debugElement) {
        // Initialize the 2d context here, instead of the constructor, to save some testing headaches
        if (!this.ctx) {
            const ctx = this.canvas.getContext('2d');
            (0, toolbox_1.invariant)(ctx, 'Unable to capture 2D rendering context');
            this.ctx = ctx;
        }
        this.adjustCanvas(debugElement);
        this.transferFirstPairOfStripes(source);
        this.transferSecondPairOfStripes(source);
    }
    adjustCanvas(debugElement) {
        if (this.canvas.width !== this.layout.canvasWidth ||
            this.canvas.height !== this.layout.canvasHeight) {
            this.canvas.width = this.layout.canvasWidth;
            this.canvas.height = this.layout.canvasHeight;
            if (debugElement) {
                debugElement.style.width = `${this.layout.canvasWidth}px`;
                debugElement.style.height = `${this.layout.canvasHeight}px`;
            }
        }
    }
    transferFirstPairOfStripes(source) {
        const { ctx, layout } = this;
        const sourceRect = layout.firstStripePairSourceRect();
        const destRect = layout.firstStripePairDestinationRect();
        ctx.drawImage(source, sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height, destRect.x, destRect.y, destRect.width, destRect.height);
    }
    transferSecondPairOfStripes(source) {
        const { ctx, layout } = this;
        const sourceRect = layout.secondStripePairSourceRect();
        const destRect = layout.secondStripePairDestinationRect();
        ctx.drawImage(source, sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height, destRect.x, destRect.y, destRect.width, destRect.height);
    }
    getImageData() {
        return this.ctx.getImageData(0, 0, this.layout.canvasWidth, this.layout.canvasHeight);
    }
}
exports.LightshowV2Canvasser = LightshowV2Canvasser;
/**
 * Uses the provided layout and canvasser, and reads the lightshow values as integers.
 */
class LightshowV2Extractor {
    constructor(layout, canvasser) {
        this.layout = layout;
        this.canvasser = canvasser;
    }
    extractLightshowIntegers() {
        const imageData = this.canvasser.getImageData();
        // This will hold the 8 32-bit floats that LightshowV2 encodes in the image data
        const recoveredValues = [];
        let currentValue = 0;
        // Roll through each stripe, and each of their 64 bits, producing two floats per stripe
        for (let stripe = 0; stripe < 4; stripe++) {
            for (let bit = 0; bit < 64; bit++) {
                const x = this.layout.getBitX(stripe, bit);
                const y = this.layout.getBitY(stripe, bit);
                currentValue <<= 1;
                currentValue += (0, lightshow_utils_1.isDark)(x, y, imageData) ? 0 : 1;
                // Save off the current value when we have 32 bits (twice per stripe)
                if (bit === 31 || bit === 63) {
                    recoveredValues.push(currentValue);
                    currentValue = 0;
                }
            }
        }
        return recoveredValues;
    }
}
exports.LightshowV2Extractor = LightshowV2Extractor;
/**
 * Converts the extracted integers into an `ICameraFrame`
 */
class LightshowV2Converter {
    constructor(layout) {
        this.layout = layout;
    }
    convertRecoveredIntegersToCamera(integers, latestDataTrackFrame) {
        var _a, _b;
        return {
            frameNumber: integers[0],
            cameraPose: {
                position: [
                    uint32ToFloat(integers[5]),
                    uint32ToFloat(integers[6]),
                    uint32ToFloat(integers[7]),
                ],
                rotation: [
                    uint32ToFloat(integers[2]),
                    uint32ToFloat(integers[3]),
                    uint32ToFloat(integers[4]),
                    uint32ToFloat(integers[1]),
                ],
            },
            cameraIntrinsics: latestDataTrackFrame === null || latestDataTrackFrame === void 0 ? void 0 : latestDataTrackFrame.cameraIntrinsics,
            cameraDimensions: (_a = latestDataTrackFrame === null || latestDataTrackFrame === void 0 ? void 0 : latestDataTrackFrame.cameraDimensions) !== null && _a !== void 0 ? _a : this.layout.sourceDimensions,
            imageUpSide: (_b = latestDataTrackFrame === null || latestDataTrackFrame === void 0 ? void 0 : latestDataTrackFrame.imageUpSide) !== null && _b !== void 0 ? _b : FrameSide.TOP,
        };
    }
}
exports.LightshowV2Converter = LightshowV2Converter;
/**
 * Some useful debugging tools, when trying to figure out what is happening
 */
// class LightshowV2Debugger {
//     constructor(private readonly layout: LightshowV2Layout) {}
//
//     // noinspection JSUnusedGlobalSymbols
//     debugLandscapeFrame(imageData: ImageData) {
//         const {
//             bitWidth,
//             canvasWidth,
//             canvasHeight,
//             halfBitWidth,
//             halfStripeHeight,
//             shortEdge,
//             stripeHeight,
//         } = this.layout;
//
//         logger.info(
//             `Running the frame number debugger... canvas is ${canvasWidth}x${canvasHeight}... first 32 bits out of short edge ${shortEdge}px means that ` +
//                 ` each bit is ${bitWidth} wide (${shortEdge} / 90 = ${
//                     shortEdge / 90.0
//                 })... half bit: ${halfBitWidth}... \n\tstripe ` +
//                 ` height: ${stripeHeight}, half: ${halfStripeHeight}, so running down y=${halfStripeHeight} for frame number, and ${
//                     3 * stripeHeight + halfStripeHeight
//                 } for Y`,
//         );
//
//         let valueF = 0;
//         const stringsF: string[] = [];
//         let valueY = 0;
//         const stringsY: string[] = [];
//
//         for (let p = 0; p < 32; p++) {
//             const y = p * bitWidth + halfBitWidth;
//
//             // Pull the frame number from the first half of the first stripe
//             const darkF = isDark(halfStripeHeight, y, imageData);
//             stringsF.push(
//                 `${p}: ${valueF} << 1 + ${darkF ? '0' : '1'} = ${valueF << 1} + ${
//                     darkF ? 0 : 1
//                 } = ${(valueF << 1) + (darkF ? 0 : 1)}`,
//             );
//             valueF <<= 1;
//             valueF += darkF ? 0 : 1;
//
//             // Pull the pose's Y value from the first half of the last stripe
//             const darkY = isDark(3 * stripeHeight + halfStripeHeight, y, imageData);
//             stringsY.push(
//                 `${p}: ${valueY} << 1 + ${darkY ? '0' : '1'} = ${valueY << 1} + ${
//                     darkY ? 0 : 1
//                 } = ${(valueY << 1) + (darkY ? 0 : 1)}`,
//             );
//             valueY <<= 1;
//             valueY += darkY ? 0 : 1;
//         }
//
//         const frameNumber = valueF;
//         logger.info(`FRAME NUMBER ${frameNumber}... data:\n${stringsF.join('\n')}`);
//
//         const yValue = uint32ToFloat(valueY);
//         logger.info(`Y VALUE ${yValue}... data:\n${stringsY.join('\n')}`);
//     }
// }
// Changed from: https://stackoverflow.com/a/43081076/194065
function uint32ToFloat(n) {
    const buf = new ArrayBuffer(4);
    const float = new Float32Array(buf);
    const uint = new Uint32Array(buf);
    uint[0] = n;
    return float[0];
}
//# sourceMappingURL=lightshow_v2.js.map